<script setup lang="ts">
import { cn, isDefined, walkTreeByLevel } from "@voltalis/frontend-utils";
import {
  type IVirtualizedTreeNode,
  type IVirtualizedTreeNodeModel,
  DraggableSiteSectionsTree,
  type ISiteTreeStructureExpose,
} from "@voltalis/ui-kit/components";
import { CubeIcon } from "@voltalis/ui-kit/icons";
import { MarkerPinIcon } from "@voltalis/ui-kit/icons";
import { computed, ref, useTemplateRef, watch } from "vue";

import { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { SiteSectionId } from "@/data/common/ids";
import { SectionForest, SectionNode } from "@/domains/v4b/site-section/site-section.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { chain } from "@/utils/chain";

import Skeleton from "primevue/skeleton";

import IndeterminatedCheckbox from "@/components/ui/checkbox/indeterminated-checkbox.vue";

export interface ISimpleSiteSectionsTree {
  siteName: string | undefined;
  siteSectionsForest: SectionForest | undefined;
  isLoading?: boolean;
  autoScrollToNode?: (node: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>) => boolean;
  selectable?: boolean;
}

const props = withDefaults(defineProps<ISimpleSiteSectionsTree>(), {
  selectable: false,
});

defineSlots<{
  loader: () => unknown;
  sectionName: (props: { section: IVirtualizedTreeNodeModel<TSiteSectionsTreeData> }) => unknown;
  nodeIcon: (props: { section: IVirtualizedTreeNodeModel<TSiteSectionsTreeData> }) => unknown;
}>();

const { t, k } = useTypedIntl();

const draggableSiteSectionsTree = useTemplateRef("draggableSiteSectionsTree");
const NODE_HEIGHT = 30;
const SECTION_HEIGHT = 26;

const searchQuery = defineModel<string>("searchQuery", {
  default: "",
});
const selectedSections = defineModel<Map<SiteSectionId, string>>("selectedSections", {
  required: false,
  default: new Map(),
});
const previewedSectionsSelectIds = ref<Set<SiteSectionId>>(new Set());

function mapSectionToVirtualizedTreeNode(sectionNode: SectionNode): IVirtualizedTreeNode<SectionNode> {
  return {
    key: sectionNode.id,
    name: sectionNode.name,
    state: {
      isExpanded: true,
    },
    children: sectionNode.children.map(mapSectionToVirtualizedTreeNode),
    data: sectionNode,
  };
}

const mappedSiteSectionTree = computed<IVirtualizedTreeNode<TSiteSectionsTreeData> | undefined>(() => {
  if (!props.siteSectionsForest || !props.siteName) return;

  return {
    name: props.siteName,
    state: {
      isExpanded: true,
    },
    children: props.siteSectionsForest.sections.map(mapSectionToVirtualizedTreeNode),
    data: null,
  };
});

function filterSectionByName(query: string, node: IVirtualizedTreeNode<TSiteSectionsTreeData>): boolean {
  const nodeName = `${node.name}`;
  return nodeName.toLowerCase().includes(query.toLowerCase());
}

const filteredNodes = computed<Array<IVirtualizedTreeNode<TSiteSectionsTreeData>>>(() => {
  if (!mappedSiteSectionTree.value) {
    return [];
  }

  if (searchQuery.value.length === 0) {
    return mappedSiteSectionTree.value.children ?? [];
  }

  function buildNode(
    node: IVirtualizedTreeNode<TSiteSectionsTreeData>,
  ): IVirtualizedTreeNode<TSiteSectionsTreeData> | null {
    const isValid = filterSectionByName(searchQuery.value, node);

    if (isValid) {
      return node;
    }

    if (!node.children) {
      return null;
    }

    const filteredChildren = chain(node.children).filterMap(buildNode).value();

    if (filteredChildren.length === 0) {
      return null;
    }

    return {
      ...node,
      children: filteredChildren,
    };
  }

  return mappedSiteSectionTree.value.children?.map((n) => buildNode(n)).filter((node) => isDefined(node)) ?? [];
});

const siteStructure = computed<IVirtualizedTreeNode<TSiteSectionsTreeData> | undefined>(() => {
  if (!mappedSiteSectionTree.value) return undefined;

  return {
    ...mappedSiteSectionTree.value,
    children: filteredNodes.value,
  };
});

async function refreshTree(): Promise<void> {
  const tree = draggableSiteSectionsTree.value;
  if (!tree) return;

  await tree.forceUpdate();

  if (props.autoScrollToNode) tree.scrollToNode(props.autoScrollToNode);
}

function isNodeRootSite(node: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>): boolean {
  return node.data === null;
}

function handlePreviewSectionSelect(section: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>) {
  if (!section.data) return;

  previewedSectionsSelectIds.value.add(section.data.id);

  for (const child of walkTreeByLevel(section.children)) {
    if (child.data) previewedSectionsSelectIds.value.add(child.data?.id);
  }
}

function resetPreviewSectionSelect() {
  previewedSectionsSelectIds.value.clear();
}

function isSectionPreviewedForSelection(section: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>): boolean {
  if (section.data && previewedSectionsSelectIds.value.has(section.data.id)) return true;
  return false;
}

function deepToggleSectionChildren(section: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>, select: boolean): void {
  if (!section.children?.length) return;

  section.children.forEach((child: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>) => {
    if (!child.data) return;

    if (select) {
      selectedSections.value.set(child.data.id, child.name);
    } else if (!select) {
      selectedSections.value.delete(child.data.id);
    }

    if (child.children?.length) {
      deepToggleSectionChildren(child, select);
    }
  });
}

function handleToggleSectionSelection(section: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>) {
  if (!section.data) return;

  const previousState = getSectionSelectionValue(section);

  if (previousState === true) {
    selectedSections.value.delete(section.data.id);
    deepToggleSectionChildren(section, false);
  } else {
    selectedSections.value.set(section.data.id, section.name);
    deepToggleSectionChildren(section, true);
  }
}

function getSectionSelectionValue(section: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>): boolean | null {
  if (!section.data) return false;

  if (selectedSections.value.get(section.data.id)) {
    return true;
  }

  if (
    section.children.some(
      (childSection: IVirtualizedTreeNodeModel<TSiteSectionsTreeData>) =>
        getSectionSelectionValue(childSection) !== false,
    )
  ) {
    return null;
  }

  return false;
}

watch(
  filteredNodes,
  () => {
    void refreshTree();
  },
  {
    flush: "post",
  },
);

defineExpose<ISiteTreeStructureExpose>({
  refreshTree,
});
</script>

<template>
  <div
    class="simple-site-sections-tree"
    :data-test-id="DATA_TEST_IDS.components.simpleSiteSectionsTree.component"
  >
    <template v-if="props.isLoading">
      <slot
        :data-test-id="DATA_TEST_IDS.components.simpleSiteSectionsTree.loader"
        name="loader"
      >
        <Skeleton class="w-full h-full" />
      </slot>
    </template>

    <div
      v-else-if="!filteredNodes.length || !siteStructure"
      class="w-full h-full flex items-center justify-center text-gray-500"
      :data-test-id="DATA_TEST_IDS.components.simpleSiteSectionsTree.noResultText"
    >
      {{ t(k.global.misc.noResult) }}
    </div>

    <template v-else>
      <DraggableSiteSectionsTree
        ref="draggableSiteSectionsTree"
        :data-test-id="DATA_TEST_IDS.components.simpleSiteSectionsTree.siteSectionTree"
        :sectionsTree="siteStructure"
        :nodeHeight="NODE_HEIGHT"
        :sectionHeight="SECTION_HEIGHT"
      >
        <template #loader>
          <Skeleton class="w-full h-full" />
        </template>
        <template #nodeIcon="{ section }">
          <MarkerPinIcon
            v-if="isNodeRootSite(section)"
            class="node-icon small-cube"
            :data-test-id="DATA_TEST_IDS.components.simpleSiteSectionsTree.siteIcon"
          />
          <CubeIcon
            v-else
            class="node-icon small-cube"
            :data-test-id="DATA_TEST_IDS.components.simpleSiteSectionsTree.sectionIcon"
          />
        </template>
        <template #sectionName="{ section }">
          <div class="flex items-center w-full h-full justify-between">
            <label
              :id="`${section.key}-label`"
              :for="`${section.key}`"
              class="overflow-hidden text-ellipsis whitespace-nowrap text-text-secondary flex-grow h-full flex items-center"
              :class="{
                'font-semibold': isNodeRootSite(section),
                'cursor-pointer': props.selectable && !isNodeRootSite(section),
              }"
              :data-test-id="DATA_TEST_IDS.components.simpleSiteSectionsTree.nodeLabel"
              @mouseenter="handlePreviewSectionSelect(section)"
              @mouseleave="resetPreviewSectionSelect()"
            >
              {{ section.name }}
            </label>

            <IndeterminatedCheckbox
              v-if="props.selectable && !isNodeRootSite(section)"
              :class="
                cn({
                  'is-preview-select': isSectionPreviewedForSelection(section),
                })
              "
              :data-test-id="DATA_TEST_IDS.components.simpleSiteSectionsTree.nodeIndeterminateCheckbox"
              :model-value="getSectionSelectionValue(section)"
              :input-id="`${section.key}`"
              :aria-labelledby="`${section.key}-label`"
              @click="handleToggleSectionSelection(section)"
            />
          </div>
        </template>
      </DraggableSiteSectionsTree>
    </template>
  </div>
</template>

<style lang="scss">
.simple-site-sections-tree {
  .tree-node-padding {
    margin-left: 20px !important;
  }

  .site-section-item {
    border: none;
    border-radius: 8px;
    padding-left: 4px;
    padding-right: 4px;

    &:hover {
      background-color: #3941470d;
    }
  }

  .site-section-item-content {
    gap: 6px;
  }

  .small-cube {
    width: 16px;
    height: 16px;
  }

  .node-icon {
    flex-shrink: 0;
  }

  .is-preview-select {
    .indeterminated-checkbox-box {
      border-color: #006aff;
    }
  }
}
</style>
